/**
 * Permissions Management Page
 * API-backed: roles from GET /roles, editing via DbPermissionEditor (same source as JWT).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Typography,
  Space,
  Select,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  message,
  Button,
} from 'antd';
import {
  SafetyCertificateOutlined,
  UserOutlined,
  TeamOutlined,
  AuditOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import { DbPermissionEditor } from '@/components/permissions/DbPermissionEditor';
import roleService, { Role, RoleStatistics } from '@/services/role.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;

export const PermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const refreshSession = useAuthStore((s) => s.refreshSession);
  const authCompanyId = useAuthStore((s) => s.user?.company_id);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [stats, setStats] = useState<RoleStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsVersion, setStatsVersion] = useState(0);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const list = await roleService.getRoles({
        is_active: true,
        company_id: authCompanyId || undefined,
      });
      setRoles(Array.isArray(list) ? list : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load roles');
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [authCompanyId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (selectedRoleId && !roles.some((r) => r.id === selectedRoleId)) {
      setSelectedRoleId(null);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selectedRoleId) {
      setStats(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const s = await roleService.getRoleStatistics(selectedRoleId);
        if (!cancelled) setStats(s);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRoleId, statsVersion]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId]
  );

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: `${r.name} (${r.code})` })),
    [roles]
  );

  const handlePermissionsSaved = async () => {
    try {
      await refreshSession();
      message.success(
        'Permissions saved — your session was refreshed so the sidebar uses the new permissions.'
      );
    } catch {
      message.warning(
        'Permissions saved. Refresh the page or sign out and sign in so menus pick up the changes.'
      );
    }
    await loadRoles();
    setStatsVersion((v) => v + 1);
  };

  return (
    <div style={{ padding: '24px' }}>
      <SmartBreadcrumbs />

      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/platform/security/users')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Back to users
      </Button>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Row justify="space-between" align="middle">
            <Col>
              <Space direction="vertical" size={0}>
                <Title level={3} style={{ margin: 0 }}>
                  <SafetyCertificateOutlined /> Permissions Management
                </Title>
                <Text type="secondary">
                  Manage role-based access control (RBAC) and permissions
                </Text>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card size="small">
          <Spin spinning={rolesLoading}>
            <Row gutter={16} align="middle">
              <Col xs={24} md={10} lg={8}>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Text strong>Select Role to Manage</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Choose a role"
                    value={selectedRoleId}
                    onChange={setSelectedRoleId}
                    options={roleOptions}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Space>
              </Col>
              <Col xs={24} md={14} lg={16}>
                {selectedRoleId && (
                  <Spin spinning={statsLoading}>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic
                          title="Total Permissions"
                          value={stats?.permissionCount ?? 0}
                          prefix={<SafetyCertificateOutlined />}
                          valueStyle={{ fontSize: 20 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="Modules"
                          value={stats?.moduleCount ?? 0}
                          prefix={<TeamOutlined />}
                          valueStyle={{ fontSize: 20 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="Users"
                          value={stats?.userCount ?? 0}
                          prefix={<UserOutlined />}
                          valueStyle={{ fontSize: 20 }}
                        />
                      </Col>
                    </Row>
                  </Spin>
                )}
              </Col>
            </Row>
          </Spin>
        </Card>

        {selectedRole ? (
          <Card title={`${selectedRole.name} — permissions`}>
            <DbPermissionEditor
              key={selectedRole.id}
              roleId={selectedRole.id}
              roleName={selectedRole.name}
              isSystemRole={selectedRole.is_system_role}
              onSaved={handlePermissionsSaved}
            />
          </Card>
        ) : (
          <Card>
            <Alert
              message="No Role Selected"
              description="Please select a role from the dropdown above to manage its permissions."
              type="info"
              showIcon
              icon={<SafetyCertificateOutlined />}
            />
          </Card>
        )}

        <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
          <Space direction="vertical" size="small">
            <Text strong>
              <AuditOutlined /> Quick Help
            </Text>
            <Text type="secondary">
              • <Text strong>Search:</Text> Filter the permission list by code, name, or module
            </Text>
            <Text type="secondary">
              • <Text strong>Modules:</Text> Expand each module and use “All” to toggle every permission in that group
            </Text>
            <Text type="secondary">
              • <Text strong>Save:</Text> Click “Save permissions” — changes apply to JWT on next sign-in; your session
              refreshes after save when possible
            </Text>
            <Text type="secondary">
              • <Text strong>System roles:</Text> Clone a template role on the Roles page to customize permissions
            </Text>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default PermissionsPage;
